import { useEffect, useMemo } from 'react'

import { getGivingActionLogEvents } from '@percent/workplace-giving/api/account/givingActions/getGivingActionLogEvents/getGivingActionLogEvents'
import type { GivingActionRow } from '@percent/workplace-giving/common/components/GivingActionCard/GivingActionCard.types'
import { useLogger, useQuery } from '@percent/workplace-giving/common/hooks'

export const GIVING_ACTION_LOG_EVENTS_QUERY_KEY = 'getGivingActionLogEvents'

export type UseGivingActionLogEventsProps = Readonly<{
  row: GivingActionRow | null
}>

export type UseGivingActionLogEvents = Readonly<{
  loggedDates: Date[]
  isLoading: boolean
}>

/** Local days already logged for one action + target. Only `daily` actions have per-day history. */
export const useGivingActionLogEvents = ({ row }: UseGivingActionLogEventsProps): UseGivingActionLogEvents => {
  const { logError } = useLogger()
  const enabled = !!row && row.frequency === 'daily'

  const { data, isPending, isError, error } = useQuery(
    [
      GIVING_ACTION_LOG_EVENTS_QUERY_KEY,
      {
        actionId: row?.actionId ?? '',
        targetType: row?.targetType ?? 'campaign',
        targetId: row?.targetId ?? ''
      }
    ],
    getGivingActionLogEvents,
    { enabled }
  )

  useEffect(() => {
    if (isError && error) {
      logError(error)
    }
  }, [isError, error, logError])

  const loggedDates = useMemo(
    () => data?.data.map(logEvent => new Date(logEvent.completionPeriodStartedAt)) ?? [],
    [data]
  )

  return {
    loggedDates,
    isLoading: enabled && isPending
  }
}
